import {assertAndContinue} from 'Asserts';
import {AnalyticsParameterValueOperation} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AnalyticsParameterValue as ParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import getParameterValueObject from 'insights/parameters/data/getParameterValueObject';

import {AnalyticsParameterValueTemplateGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterValueTemplateGraphQL';

type ParameterConfig = ExtractByTypename<
  AnalyticsParameterValueTemplateGraphQL['config'],
  'AnalyticsParameterBioentityLinkConfigNode'
>;

type ParameterValueTemplateWithAccessibleConfig = AnalyticsParameterValueTemplateGraphQL & {
  config: ParameterConfig;
};

/**
 * Converts a list of AnalyticsParameterValueTemplates to a list of AnalyticsParameterValues that can be
 * used to override parameters in a dashboard. It does so by interpreting the different AnalyticsParameterValueOperation
 * types and the embedding data to generate the parameter list.
 *
 * The function assumes the user has access to all the parameterConfigs in the embedding, which comes from the
 * dashboards permissions. We simply filter these out if they do occur, but this function does not make sense
 * unless someone has full access to the embedded dashboard because it will just return an empty list [].
 */
export default function parameterValueTemplatesToOverrides(
  parameterValueTemplates: Array<AnalyticsParameterValueTemplateGraphQL>,
  linkedItemId: string
): Array<ParameterValue> {
  const accessibleConfigValueTemplates = parameterValueTemplates.filter(
    (parameterValueTemplate) => !isInaccessible(parameterValueTemplate.config)
  );

  assertAndContinue(
    accessibleConfigValueTemplates.length === parameterValueTemplates.length,
    'parameterValueTemplatesToOverrides should not be used in a context where some parameterConfigs are inaccessible'
  );

  return accessibleConfigValueTemplates.map(
    (parameterValueTemplate: ParameterValueTemplateWithAccessibleConfig) =>
      getParameterValueObject(
        parameterValueTemplate.config,
        parameterValueTemplate.valueOperation === AnalyticsParameterValueOperation.USE_EMBEDDING_ITEM
          ? linkedItemId
          : parameterValueTemplate.valueJson
      )
  );
}
